/**
 * 强大的WebSocket
 */
import Backoff from 'backo2';
import Emitter from 'component-emitter';
import bind from 'component-bind';

/**
 * subscriptions 辅助函数
 * @param  {Object}   obj 对象
 * @param  {String}   ev  事件
 * @param  {Function} fn  事件触发回调函数
 * @return {Object}       返回具有销毁功能的对象
 */
function on (obj, ev, fn) {
  obj.on(ev, fn);
  return {
    destroy: function () {
      obj.removeListener(ev, fn);
    }
  };
}

export default WS;

/**
 * Mix in `Emitter`.
 */

Emitter(WS.prototype);
Emitter(WebSocket.prototype);

/**
 * `WS` 构造函数.
 * @param {String} uri  WebSocket uri
 * @param {Object} opts 初始化参数
 * @api public
 */
function WS (uri, opts) {
  if (uri && ('object' === typeof uri)) {
    opts = uri;
    uri = opts.uri;
  }
  opts = opts || {};

  opts.path = opts.path || '/';
  this.subs = []; // 订阅对象数组
  this.opts = opts;

  this.connected = false; // 链接的状态

  this.readyState = 'closed'; // 开始的状态
  this.uri = uri; // socket 的地址
  this.connecting = []; // 连接中的数组
  this.lastPing = null; // 最后的 ping

  this.autoConnect = opts.autoConnect !== false; // 是否自动打开
  this.debug = opts.debug || true; // 是否是 debug 模式
  this.handleError = null;
  this.handleMessage = null;
  this.intervalId = null;
  this.reconnecting = false;
  this.skipReconnect = false;
  this.engine = null; // webSocket

  this.reconnectionAttempts(opts.reconnectionAttempts || Infinity); // 最大重连次数
  this.reconnection(opts.reconnection !== false);
  this.reconnectionDelay(opts.reconnectionDelay || 1000); // 延迟时间
  this.reconnectionDelayMax(opts.reconnectionDelayMax || 5000); // 最大延迟时间
  this.randomizationFactor(opts.randomizationFactor || 0.5); // 延迟随机增长数

  this.backoff = new Backoff({
    min: this.reconnectionDelay(),
    max: this.reconnectionDelayMax(),
    jitter: this.randomizationFactor()
  });

  this.timeout(null == opts.timeout ? 10000 : opts.timeout); // 超时处理

  if (this.autoConnect) this.open();
  if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', '创建 socket 实例');
}

// open
WS.prototype.open = function (fn, opts) {
  var self = this;

  if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', 'open 开始状态:', this.readyState, '连接地址:', this.uri);

  if (~this.readyState.indexOf('open')) return this;

  try {
    this.engine = new WebSocket(this.uri);
  } catch (err) {
    if (this.debug) { console.error('[socket]创建实例发生错误', err);}
    return this.onError(err);
  }

  var socket = this.engine;
  this.readyState = 'opening';
  this.skipReconnect = false;

  // 添加 open 事件
  var openSub = on(socket, 'onopen', function () {
    if (self.debug) console.log('%c[socket]', 'color: #49c9c9;', 'openSub 执行');
    self.onOpen();
    fn && fn();
  });

  // 添加 error 事件
  var errorSub = on(socket, 'onerror', function (data) {
    if (self.debug) console.log('%c[socket]', 'color: #49c9c9;', 'errorSub 执行');

    self.clearUp();
    self.readyState = 'closed';

    self.emit('connect_error', data); // TODO: 发射 链接失败 事件 建议改成 error

    if (fn) {
      var err = new Error('Connection error');
      err.data = data;
      fn(err);
    } else {
      self.maybeReconnectOnOpen();
    }
  });

  // 添加链接超时机制，iOS 平台不会被动触发 error 事件
  if (false !== this._timeout) {
    var timeout = this._timeout;
    if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', '超时将在开始重连', timeout);

    var timer = setTimeout(function () {
      socket.emit('onerror', 'timeout'); // TODO: 发射 链接错误
      self.emit('connect_timeout', timeout); // TODO: 发射 链接超时 事件
    }, timeout);

    this.subs.push({
      destroy: function () {
        clearTimeout(timer);
      }
    });
  }

  this.subs.push(openSub);
  this.subs.push(errorSub);
  this.addEventListeners();
  return this;
};

// socket open
WS.prototype.onOpen = function () {
  if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', 'WS prototype onOpen 执行');

  this.setPing();
  this.clearUp();

  this.readyState = 'open'; // 更新状态
  this.emit('open'); // TODO: 发射 open 事件

  this.onConnect();

  // 添加事件监听
  var socket = this.engine;
  this.subs.push(on(socket, 'onmessage', bind(this, 'onMessage')));
  this.subs.push(on(socket, 'onping', bind(this, 'onPing')));
  this.subs.push(on(socket, 'onpong', bind(this, 'onPong')));
  // this.subs.push(on(socket, 'onerror', bind(this, 'onError')));
  this.subs.push(on(socket, 'onclose', bind(this, 'close')));
};

// socket connect 监听
WS.prototype.onConnect = function () {
  if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', 'WS prototype onConnect 执行');

  this.connected = true;
  this.disconnected = false; // FIXME: 属性是用来干嘛的？
  this.emit('connect'); // TODO: 发射链接事件
};

// socket message 监听
WS.prototype.onMessage = function (data) {
  if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', 'WS prototype onMessage 执行');

  this.emit('message', data); // TODO: 发射 消息 事件
};

// socket close
WS.prototype.close = function (reason) {
  if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', 'WS prototype close 执行:', reason);

  this.reconnecting = false;

  if ('opening' === this.readyState) {
    this.clearUp();
  }

  this.backoff.reset();
  this.readyState = 'closed';

  window.clearInterval(this.pingIntervalTimer);

  if (this.connected) {
    this.onClose(reason || 'io client disconnect');
  }

  return this;
};

// socket close 监听
WS.prototype.onClose = function (reason) {
  if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', 'WS prototype onClose 执行:', reason);

  this.clearUp();
  this.backoff.reset();
  this.readyState = 'closed';
  this.emit('close', reason); // TODO: 发射 关闭 socket 事件

  switch (reason) {
    case 1000:
      this.skipReconnect = true;
      if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', '用户主动关闭', reason);
    default:
      if (this._reconnection && !this.skipReconnect) {
      this.reconnect();
    }
  }
};

// socket reconnect
WS.prototype.reconnect = function () {
  if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', 'WS prototype reconnect 执行:');

  if (this.reconnecting || this.skipReconnect) {
    return this;
  }

  if (this.backoff.attempts > this._reconnectionAttempts) {
    if (this.debug) console.error('[socket]超过最大重连次数, 重连彻底失败');

    window.clearInterval(this.pingIntervalTimer);
    this.clearUp();
    this.backoff.reset();
    this.emit('reconnect_failed'); // TODO: 发送 重连彻底 事件
    this.reconnecting = false;
  } else {
    var delay = this.backoff.duration();
    if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', '第几次重连：', this.backoff.attempts, '等待 %dms 开始发起重连：', delay);

    this.reconnecting = true;

    var timer = setTimeout(() => {
      if (this.skipReconnect) return;

      this.emit('reconnect_attempt', this.backoff.attempts); // TODO: 发送 重连次数 事件
      this.emit('reconnecting', this.backoff.attempts); // // TODO: 发送事件

      // FIXME: 跟上面是否冲突 ？
      if (this.skipReconnect) return;

      this.open((err) => {
        if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', '打开重连 open');
        this.reconnecting = false;

        if (err) {
          err.attempts = this.backoff.attempts;

          if (this.debug) console.log('[socket]重连 open 失败', err);

          this.emit('reconnect_error', err); // TODO: 发送 重连错误 事件
          this.reconnect(); // 递归调用
        } else {
          if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', '重连成功');
          this.onReconnect();
        }
      });
    }, delay);

    this.subs.push({
      destroy: function () {
        clearTimeout(timer);
      }
    });
  }
};

// socket reconnect 监听
WS.prototype.onReconnect = function () {
  if (this.debug) console.log('%c[socket]', 'color: #49c9c9;', 'WS prototype onReconnect 执行:');

  var attempt = this.backoff.attempts;
  this.reconnecting = false;
  this.backoff.reset();
  this.emit('reconnect_success', attempt); // TODO: 发射事件
};

// socket ping 事件
WS.prototype.onPing = function () {
  this.lastPing = new Date();
  this.emit('onping');
};

// socket pong 事件
WS.prototype.onPong = function () {
  this.emit('onpong', new Date() - this.lastPing);
};

// 设置重连
WS.prototype.reconnection = function (v) {
  if (!arguments.length) return this._reconnection;
  this._reconnection = !!v;
  return this;
};

// 设置尝试重连配置
WS.prototype.reconnectionAttempts = function (v) {
  if (!arguments.length) return this._reconnectionAttempts;
  this._reconnectionAttempts = v;
  return this;
};

// 设置重连延迟
WS.prototype.reconnectionDelay = function (v) {
  if (!arguments.length) return this._reconnectionDelay;
  this._reconnectionDelay = v;
  this.backoff && this.backoff.setMin(v);
  return this;
};

// 设置随机数
WS.prototype.randomizationFactor = function (v) {
  if (!arguments.length) return this._randomizationFactor;
  this._randomizationFactor = v;
  this.backoff && this.backoff.setJitter(v);
  return this;
};

// 设置重连最大延迟
WS.prototype.reconnectionDelayMax = function (v) {
  if (!arguments.length) return this._reconnectionDelayMax;
  this._reconnectionDelayMax = v;
  this.backoff && this.backoff.setMax(v);
  return this;
};

// 设置超时
WS.prototype.timeout = function (v) {
  if (!arguments.length) return this._timeout;
  this._timeout = v;
  return this;
};

// 方法 尝试重连
WS.prototype.maybeReconnectOnOpen = function () {
  if (!this.reconnecting && this._reconnection && this.backoff.attempts === 0) {
    this.reconnect();
  }
};

// 设置ping机制
WS.prototype.setPing = function () {
  var self = this;
  window.clearInterval(self.pingIntervalTimer);
  self.pingIntervalTimer = setInterval(function () {
    self.ping();
  }, 10000);
};

// 方法 ping
WS.prototype.ping = function () {
  var self = this;
  this.engine.send('PING');
};

// 方法 destory
WS.prototype.destroy = function (socket) {
  var index = indexOf(this.connecting, socket);
  if (~index) this.connecting.splice(index, 1);
  if (this.connecting.length) return;
  this.close();
};

// 方法 clearUp
WS.prototype.clearUp = function () {
  if (this.debug) console.log('ws-->clearUp');

  var subsLength = this.subs.length;
  for (var i = 0; i < subsLength; i++) {
    var sub = this.subs.shift();
    sub.destroy();
  }

  this.lastPing = null;
};

// 方法 send
WS.prototype.send = function (msg) {
  this.engine.send(msg);
}

// 添加socket 监听
WS.prototype.addEventListeners = function () {
  var self = this;

  this.engine.onopen = function () {
    self.engine.emit('onopen');
  };

  this.engine.onclose = function () {
    self.skipReconnect = false;
    self.engine.emit('onclose', 'connet close');
  };

  this.engine.onmessage = function (ev) {
    if (ev.data !== 'PONG') {
      self.engine.emit('onmessage', ev.data);
    }
  };

  this.engine.onerror = function (e) {
    self.engine.emit('onerror', e);
  };
};

/**
 * open 方法
 */

// WS.prototype.open =
// WS.prototype.connect = function (fn, opts) {
//   var self = this;
//   console.info('开始状态 %s', this.readyState);

//   if (~this.readyState.indexOf('open')) return this;

//   console.log('opening ==>', this.uri);

//   try {
//     this.engine = new WebSocket(this.uri);
//   } catch (err) {
//     console.error('连接发生错误 ==========>', err);
//     return this.onError(err);
//   }

//   var socket = this.engine;
//   this.readyState = 'opening';
//   this.skipReconnect = false;

//   // 添加 open 事件
//   var openSub = on(socket, 'onopen', function () {
//     console.info('socket 链接打开');
//     self.onOpen();
//     fn && fn();
//   });

//   // 添加 error 事件
//   var errorSub = on(socket, 'error', function (data) {
//     console.error('socket 链接出错');
//     self.clearUp();
//     self.readyState = 'closed';
//     self.emit('connect_error', data);
//     if (fn) {
//       var err = new Error('Connection error');
//       err.data = data;
//       fn(err);
//     } else {
//       self.maybeReconnectOnOpen();
//     }
//   });

//   // 添加链接超时机制，iOS 平台不会被动触发 error 事件
//   if (false !== this._timeout) {
//     var timeout = this._timeout;
//     console.log('connect attempt will timeout after %d', timeout);

//     var timer = setTimeout(function () {
//       console.log('connect attempt timed out after %d', timeout);
//       socket.emit('error', 'timeout');
//       self.emit('connect_timeout', timeout);
//     }, timeout);

//     this.subs.push({
//       destroy: function () {
//         clearTimeout(timer);
//       }
//     });
//   }

//   this.subs.push(openSub);
//   this.subs.push(errorSub);
//   this.addEventListeners();
//   return this;
// };

// socket error 事件
// WS.prototype.onError = function (err) {
//   if (debug) console.error('监听 ws-socket 出错', err);
//   this.emit('onerror', err);
// };




// WEBPACK FOOTER //
// ./src/lib/ws.io.js